import React, { use, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faFileLines, faL, faTrash } from '@fortawesome/free-solid-svg-icons';

import { toast } from 'react-toastify';
import useFetch from '../../../hooks/use-fetch';
import { Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { request } from '../../../axiosConfig';
import DeleteModal from '../../Modals/DeleteModal';
import { ReportData, useReports } from '../../../context/ReportContext';
import { AxiosError } from 'axios';


export function formatDateTime(isoString: string | null): string | null {
  if (!isoString) return null;

  const date = new Date(isoString);

  const pad = (n: number) => n.toString().padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();

  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}



const statusMap = {
  "ongoing": "In Corso",
  "error": "Errore",
  "ready": "Pronto"
}


type Order = "asc" | "desc";

export const ReportsTable = () => {
  const {reports, setReports} = useReports()

  const [filteredReports, setFilteredReports] = useState<ReportData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([])
  const [selectedKind, setSelectedKind] = useState<('fesr' | 'avvni' | null)[]>([])
  const [orderBy, setOrderBy] = useState<keyof ReportData>("updated_at");
  const [order, setOrder] = useState<Order>("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [reportToDelete, setReportToDelete] = useState<ReportData | null>(null)

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [selectedStatuses, selectedKind, searchTerm])

  useEffect(() => {
      const noStatus = selectedStatuses.length === 0
      const noKind = selectedKind.length === 0
      const noSearch = !searchTerm || searchTerm.trim() === ""
      
      let updated = reports
      
      if (!(noStatus && noKind && noSearch)) {
        let lower = searchTerm.toLowerCase()
        updated = reports.filter((r) => {
          const matchesStatus = noStatus || selectedStatuses.includes(r.status)
          const matchesKind = noKind || selectedKind.includes(r.kind)
          const matchesSearch =
            noSearch ||
            r.company_name.toLowerCase().includes(lower) ||
            r.cup.toLowerCase().includes(lower) ||
            r.creator?.email.toLowerCase().includes(lower)

          return matchesStatus && matchesKind && matchesSearch
        });
      }

      setFilteredReports(updated);
    }, [reports, selectedStatuses, selectedKind, searchTerm])

  const handleSort = (property: keyof ReportData) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);

    const sorted = [...filteredReports].sort((a, b) => {
      if (a[property]! < b[property]!) return isAsc ? -1 : 1;
      if (a[property]! > b[property]!) return isAsc ? 1 : -1;
      return 0;
    });
    setFilteredReports(sorted);
  };

  const handleStatusSelect = (selection: string) => {
    if(selectedStatuses.includes(selection)){
      setSelectedStatuses(selectedStatuses.filter(s => (s !== selection)))
    } else {
      setSelectedStatuses([...selectedStatuses, selection])
    }
  }

  const handleKindSelect = (selection: 'fesr'| 'avvni') => {
    if(selectedKind.includes(selection)){
      setSelectedKind(selectedKind.filter(s => (s !== selection)))
    } else {
      setSelectedKind([...selectedKind, selection])
    }
  }

  const handleDownloadFile = async (reportId: string, companyName: string, updatedAt: string, isExport: boolean=true) => {
    if (!reportId) return;
    const timestamp = updatedAt.replace(/[:.]/g, "-");
    let url = ''
    let filename = ''
    let fileType = ''
    if (isExport){
      url = `/download/${reportId}`
      filename = `export_${companyName}_${timestamp}.xlsx`
      fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    } else {
      url = `/download/${reportId}/report/`
      filename = `report_${companyName}_${timestamp}.csv`
      fileType = 'text/csv;charset=utf-8;'
    }

    try {
      const response = await request.get(url, {responseType: 'blob'});
      const file = new File(
          [response.data],
          filename,
          { type:  fileType}
        );

      const fileUrl = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();

      URL.revokeObjectURL(fileUrl);
    } catch (err) {
      let error = err as AxiosError
      if (error.status == 428){
        toast.error('Il formato del report è cambiato. \nSi prega di ripetere la generazione')
      } else {
        toast.error('Errore durante il download.');
      }
    }
  }

  const handleDelete = async () => {
    if (!reportToDelete) return;
    try {
      const response = await request.delete(`/reports/${reportToDelete.id}`)
    } catch (err){
      toast.error('Errore durante l\'eliminazione del report')
      return
    }
    setReports(reports.filter(r => (r.id !== reportToDelete.id)))
    setFilteredReports(reports.filter(r => (r.id !== reportToDelete.id)))
    setReportToDelete(null)
    setIsDeleteModalOpen(false)
    toast.info("Report eliminato")
  }

  return (
    <div>
      {isDeleteModalOpen &&
          <DeleteModal 
              title={'Elimina'} 
              description={
              <>
                  <p>Sei sicuro di voler eliminare il report:</p>
                  <p>
                    <strong>{reportToDelete?.company_name}</strong> 
                    del {reportToDelete?.updated_at ? formatDateTime(reportToDelete.updated_at) : ''}
                  </p>
              </>
              }
              onConfirm={handleDelete}
              onCancel={()=>setIsDeleteModalOpen(false)}
          />
      }
      <div>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Cerca report"
          className="search-input"
        />
      </div>
      
      <div  style={{margin: '20px 10px', display: 'flex', gap: '12px'}}>
        <Chip 
            key='fesr'
            label="FESR" 
            onClick={() => handleKindSelect('fesr')} 
            color='error' 
            variant={selectedKind.includes('fesr') ? 'filled' : 'outlined'}
            sx={{minWidth: '150px'}}
        />
        <Chip 
            key='avvni'
            label="Avv. Nuova Impresa" 
            onClick={() => handleKindSelect('avvni')} 
            color='error' 
            variant={selectedKind.includes('avvni') ? 'filled' : 'outlined'}
            sx={{minWidth: '150px'}}
        />
      </div>
      <div  style={{margin: '20px 10px', display: 'flex', gap: '12px'}}>
      {
        Object.entries(statusMap).map(([key, value]) => {
            const k = key;
            const isSelected = selectedStatuses.includes(k)
            return(
              <Chip 
                key={k}
                label={value} 
                onClick={() => handleStatusSelect(k)} 
                color='error' 
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{minWidth: '150px'}}
            />
        )})
      }
      </div>
      

      <TableContainer 
        component={Paper} 
        sx={{ flex: 1, width: '1000pt', height: '100%', borderRadius:'16px'}}
        >
        <Table>
          <TableHead>
            <TableRow>
              {[
                { id: "id", label: "ID" },
                { id: "company_name", label: "Nome" },
                { id: "cup", label: "CUP" },
                { id: "kind", label: "Tipologia" },
                { id: "status", label: "Stato" },
                { id: "updated_at", label: "Data" },
                { id: "user", label: "Utente" },
                { id: "action", label: ''}
              ].map((col) => (
                <TableCell key={col.id} sx={{fontSize: '14px', fontWeight: '600'}}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : "asc"}
                    onClick={() => handleSort(col.id as keyof ReportData)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
            <TableBody>
            {filteredReports
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.id.slice(0, 8)}...</TableCell>
                  <TableCell>{report.company_name}</TableCell>
                  <TableCell>{report.cup}</TableCell>
                  <TableCell>{report.kind?.toUpperCase()}</TableCell>
                  <TableCell>{report.status ? statusMap[report.status] : ''}</TableCell>
                  <TableCell>{report.updated_at ? formatDateTime(report.updated_at) : ''}</TableCell>
                  <TableCell>{report?.creator?.email}</TableCell>
                  <TableCell>
                    <div className="table-actions">
                      <FontAwesomeIcon
                        icon={faEye}
                        className={`action-icon export ${report.status !== "ready" ? "disabled" : ""}`}
                        onClick={() => {
                          if (report.status === "ready") {
                            handleDownloadFile(report.id, report.company_name, report.updated_at);
                          }
                        }}
                        style={report.status !== "ready" ? { pointerEvents: "none", opacity: 0.5 } : {}}
                      />
                      <FontAwesomeIcon
                        icon={faFileLines}
                        className={`action-icon report ${report.status !== "ready" ? "disabled" : ""}`}
                        onClick={() => {
                          if (report.status === "ready") {
                            handleDownloadFile(report.id, report.company_name, report.updated_at, false);
                          }
                        }}
                        style={report.status !== "ready" ? { pointerEvents: "none", opacity: 0.5 } : {}}
                      />
                      <FontAwesomeIcon
                        icon={faTrash}
                        className={`action-icon delete ${report.status !== "ready" ? "disabled" : ""}`}
                        onClick={() => {
                          if (report.status !== "ongoing") {
                            setIsDeleteModalOpen(true);
                            setReportToDelete(report);
                          }
                        }}
                        style={report.status === "ongoing" ? { pointerEvents: "none", opacity: 0.5 } : {}}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>

        </Table>


        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredReports.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </div>
  );
};



const Reports = () => {
  return (
    <div className="main-container">
      <div className="content-header"></div>
      <ReportsTable />
    </div>
  );
};

export default Reports